import { useMemo } from 'react';
import { getEnv } from '@adsk/acs-sc-web-platform-api';
import { useAlloyFeatureFlag } from '@adsk/alloy-provider';
import { ChartBarIcon, GearIcon } from '@adsk/alloy-react-icon';
import {
  ACCOUNT_ADMIN_PRODUCT_NAME,
  AUTOSPECS_URLS,
  BUILDING_CONNECTED_URLS,
  FEATURE_FLAGS,
  INSIGHT_PRODUCT_NAME,
  PLANGRID_URLS,
  TRADETAPP_URLS,
} from '../../../constants';
import getInsightURL from '../../../utils/getInsightURL';
import getRelativeAccountAdminURL from '../../../utils/getRelativeAccountAdminURL';
import { useAccessLevelContext } from '../../Contexts/AccessLevelContext';
import { PRODUCT_LINK_VARIANTS, ProductLinkType } from '../consts';
import AutoSpecsColorfulIcon from '../icons/AutoSpecsColorfulIcon';
import BuildingConnectedColorfulIcon from '../icons/BuildingConnectedColorfulIcon';
import PlangridColorfulIcon from '../icons/PlangridColorfulIcon';
import TradeTappColorfulIcon from '../icons/TradeTappColorfulIcon';

type ProductLinks = {
  internalProductLinks: ProductLinkType[];
  externalProductLinks: ProductLinkType[];
};

const useProductLinks = (): ProductLinks => {
  const { accountsUserIsAccAccountAdmin, accountsUserIsExecutive } =
    useAccessLevelContext();

  const currentEnv = getEnv();

  const userAccountPermissions = useMemo(
    () => ({
      accountAdmin:
        accountsUserIsAccAccountAdmin.length > 0
          ? accountsUserIsAccAccountAdmin[0].id
          : null,
      executive:
        accountsUserIsExecutive.length > 0
          ? accountsUserIsExecutive[0].id
          : null,
    }),
    [accountsUserIsAccAccountAdmin, accountsUserIsExecutive]
  );

  const { value: showAutoSpecsLink } = useAlloyFeatureFlag(
    FEATURE_FLAGS.ShowAutoSpecsLink,
    undefined
  );

  const projectsHeaderLinksConfig = useMemo<ProductLinkType[]>(
    () => [
      {
        label: ACCOUNT_ADMIN_PRODUCT_NAME,
        url: userAccountPermissions.accountAdmin
          ? getRelativeAccountAdminURL(userAccountPermissions.accountAdmin)
          : undefined,
        isVisible: userAccountPermissions.accountAdmin,
        variant: PRODUCT_LINK_VARIANTS.INTERNAL,
        icon: GearIcon,
      },
      {
        label: INSIGHT_PRODUCT_NAME,
        url: userAccountPermissions.executive
          ? getInsightURL(userAccountPermissions.executive)
          : undefined,
        isVisible: userAccountPermissions.executive,
        variant: PRODUCT_LINK_VARIANTS.INTERNAL,
        icon: ChartBarIcon,
      },
      {
        label: 'PlanGrid',
        url: PLANGRID_URLS[currentEnv],
        isVisible: true,
        icon: PlangridColorfulIcon,
        variant: PRODUCT_LINK_VARIANTS.EXTERNAL,
      },
      {
        label: 'BuildingConnected',
        url: BUILDING_CONNECTED_URLS[currentEnv],
        isVisible: true,
        icon: BuildingConnectedColorfulIcon,
        variant: PRODUCT_LINK_VARIANTS.EXTERNAL,
      },
      {
        label: 'TradeTapp',
        url: TRADETAPP_URLS[currentEnv],
        isVisible: true,
        icon: TradeTappColorfulIcon,
        variant: PRODUCT_LINK_VARIANTS.EXTERNAL,
      },
      {
        label: 'Pype AutoSpecs',
        url: AUTOSPECS_URLS[currentEnv],
        isVisible: showAutoSpecsLink,
        variant: PRODUCT_LINK_VARIANTS.EXTERNAL,
        icon: AutoSpecsColorfulIcon,
      },
    ],
    [userAccountPermissions, currentEnv, showAutoSpecsLink]
  );

  const internalProductLinks = useMemo(
    () =>
      projectsHeaderLinksConfig.filter(
        ({ isVisible, variant }) =>
          isVisible && variant === PRODUCT_LINK_VARIANTS.INTERNAL
      ),
    [projectsHeaderLinksConfig]
  );

  const externalProductLinks = useMemo(
    () =>
      projectsHeaderLinksConfig.filter(
        ({ isVisible, variant }) =>
          isVisible && variant === PRODUCT_LINK_VARIANTS.EXTERNAL
      ),
    [projectsHeaderLinksConfig]
  );

  return {
    internalProductLinks,
    externalProductLinks,
  };
};

export default useProductLinks;
